/**
 * Экспорт плоского чертежа в DWG: геометрия холста в миллиметрах и размерные линии.
 * Рамка листа и подписи, привязанные к экрану, в файл не входят.
 */

import { saveBlobAsFile } from './fileSystemSave';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const SKIP_NAMES = new Set([
  'sheet-background',
  'weld-marker',
  'snap-point',
  'resize-handle',
  'hitbox',
  'toggle-switch',
  'view-header',
  'frame-overlay',
]);

function round(value) {
  return Math.round(value * 1000) / 1000;
}

function hasSkipName(node) {
  let current = node;
  while (current && current.getClassName && current.getClassName() !== 'Stage') {
    const name = typeof current.name === 'function' ? current.name() : '';
    if (name && name.split(/\s+/).some((part) => SKIP_NAMES.has(part))) {
      return true;
    }
    current = current.getParent();
  }
  return false;
}

function toModel(node, x, y, stageScale) {
  const point = node.getAbsoluteTransform().point({ x, y });
  const scale = stageScale || 1;
  return [round(point.x / scale), round(point.y / scale)];
}

function aciColor(value) {
  const color = String(value || '').trim().toLowerCase();
  if (color === 'red' || color === '#ff0000' || color === '#d32f2f' || color === '#f44336') {
    return 1;
  }
  return 7;
}

function isGreenSnap(node) {
  const fill = String(node.fill ? node.fill() : '').trim().toLowerCase();
  return fill === 'green' || fill === '#00ff00' || fill === '#008000';
}

function clipModeOf(node) {
  if (!node || typeof node.getAttr !== 'function') return '';
  const mode = node.getAttr('clipMode');
  if (mode === 'visible' || mode === 'hidden') return mode;
  const name = typeof node.name === 'function' ? node.name() : '';
  if (name === 'fitting-visible') return 'visible';
  if (name === 'fitting-hidden') return 'hidden';
  return '';
}

function findClipGroup(node) {
  let current = node;
  while (current && current.getClassName && current.getClassName() !== 'Stage') {
    if (clipModeOf(current)) return current;
    current = current.getParent();
  }
  return null;
}

function dashPolyline(points, onLength = 4, offLength = 2.5) {
  const pieces = [];
  let drawing = true;
  let remain = onLength;
  let current = null;
  const flush = () => {
    if (drawing && current && current.length >= 2) pieces.push(current);
    current = null;
  };
  for (let i = 0; i + 1 < points.length; i += 1) {
    let from = points[i];
    const to = points[i + 1];
    let distance = Math.hypot(to[0] - from[0], to[1] - from[1]);
    while (distance > 1e-4) {
      if (remain >= distance - 1e-4) {
        if (drawing) {
          if (!current) current = [from];
          current.push(to);
        }
        remain -= distance;
        break;
      }
      const t = remain / distance;
      const mid = [from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t];
      if (drawing) {
        if (!current) current = [from];
        current.push(mid);
        flush();
      }
      from = mid;
      distance -= remain;
      drawing = !drawing;
      remain = drawing ? onLength : offLength;
    }
  }
  flush();
  return pieces;
}

function keepNode(node) {
  if (!node.visible() || hasSkipName(node)) return false;
  if (node.opacity() < 0.5) {
    const group = findClipGroup(node);
    if (!group || clipModeOf(group) !== 'hidden') return false;
  }
  return true;
}

function rectCorners(rect) {
  const x = Number(rect.x) || 0;
  const y = Number(rect.y) || 0;
  const width = Number(rect.width) || 0;
  const height = Number(rect.height) || 0;
  return [
    [x, y],
    [x + width, y],
    [x + width, y + height],
    [x, y + height],
  ];
}

function clipSegmentToConvex(p0, p1, poly) {
  let t0 = 0;
  let t1 = 1;
  for (let i = 0; i < poly.length; i += 1) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const ex = b[0] - a[0];
    const ey = b[1] - a[1];
    const c0 = ex * (p0[1] - a[1]) - ey * (p0[0] - a[0]);
    const c1 = ex * (p1[1] - a[1]) - ey * (p1[0] - a[0]);
    const dc = c1 - c0;
    if (Math.abs(dc) < 1e-9) {
      if (c0 < -1e-6) return null;
      continue;
    }
    const t = -c0 / dc;
    if (dc < 0) {
      if (t < t1) t1 = t;
    } else if (t > t0) {
      t0 = t;
    }
    if (t0 - t1 > 1e-8) return null;
  }
  if (t1 - t0 < 1e-6) return null;
  const lerp = (t) => [p0[0] + (p1[0] - p0[0]) * t, p0[1] + (p1[1] - p0[1]) * t];
  return [lerp(Math.max(0, t0)), lerp(Math.min(1, t1))];
}

function chainSegments(segments) {
  const pieces = [];
  let current = null;
  const same = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]) < 0.05;
  segments.forEach(([a, b]) => {
    if (!a || !b || same(a, b)) return;
    if (current && same(current[current.length - 1], a)) current.push(b);
    else {
      if (current && current.length >= 2) pieces.push(current);
      current = [a, b];
    }
  });
  if (current && current.length >= 2) pieces.push(current);
  return pieces;
}

function clipPolylineInside(points, poly) {
  const segments = [];
  for (let i = 0; i + 1 < points.length; i += 1) {
    const segment = clipSegmentToConvex(points[i], points[i + 1], poly);
    if (segment) segments.push(segment);
  }
  return chainSegments(segments);
}

function subtractPolyline(points, poly) {
  const segments = [];
  for (let i = 0; i + 1 < points.length; i += 1) {
    const p0 = points[i];
    const p1 = points[i + 1];
    const inside = clipSegmentToConvex(p0, p1, poly);
    if (!inside) {
      segments.push([p0, p1]);
      continue;
    }
    const dx = p1[0] - p0[0];
    const dy = p1[1] - p0[1];
    const tOf = (point) => (
      Math.abs(dx) >= Math.abs(dy)
        ? (Math.abs(dx) < 1e-9 ? 0 : (point[0] - p0[0]) / dx)
        : (point[1] - p0[1]) / dy
    );
    const lo = Math.max(0, Math.min(tOf(inside[0]), tOf(inside[1])));
    const hi = Math.min(1, Math.max(tOf(inside[0]), tOf(inside[1])));
    const lerp = (t) => [p0[0] + dx * t, p0[1] + dy * t];
    if (lo > 0.001) segments.push([p0, lerp(lo)]);
    if (hi < 0.999) segments.push([lerp(hi), p1]);
  }
  return chainSegments(segments);
}

function arrowHead(tip, base, width) {
  const dx = tip[0] - base[0];
  const dy = tip[1] - base[1];
  const length = Math.hypot(dx, dy) || 1;
  const ux = dx / length;
  const uy = dy / length;
  const px = -uy;
  const py = ux;
  const half = width / 2;
  return [
    tip,
    [round(base[0] + px * half), round(base[1] + py * half)],
    [round(base[0] - px * half), round(base[1] - py * half)],
  ];
}

function collectDrawingEntities(stage) {
  const layer = stage.getLayers()[0];
  if (!layer) {
    return { lines: [], circles: [], solids: [], texts: [] };
  }
  const stageScale = stage.scaleX() || 1;
  const lines = [];
  const circles = [];
  const solids = [];
  const texts = [];

  const pushLine = (points, { closed = false, dashed = false, color = 7 } = {}) => {
    if (!points || points.length < 2) return;
    const cleaned = [points[0]];
    points.slice(1).forEach((point) => {
      const prev = cleaned[cleaned.length - 1];
      if (Math.hypot(point[0] - prev[0], point[1] - prev[1]) >= 0.05) {
        cleaned.push(point);
      }
    });
    if (cleaned.length < 2) return;
    lines.push({ points: cleaned, closed, dashed, color });
  };

  const polygonOf = (group, rect) => rectCorners(rect).map(([x, y]) => toModel(group, x, y, stageScale));

  const emitClipped = (node, points, { dashed = false, closed = false, color = 7 } = {}) => {
    const group = findClipGroup(node);
    if (!group) {
      pushLine(points, { dashed, closed, color });
      return;
    }
    let path = points;
    if (closed && path.length > 2) {
      const first = path[0];
      const last = path[path.length - 1];
      if (Math.hypot(first[0] - last[0], first[1] - last[1]) > 0.05) {
        path = [...path, first];
      }
    }
    const rects = group.getAttr('clipRects') || [];
    const polys = rects
      .filter((rect) => rect && rect.width > 0.2 && rect.height > 0.2)
      .map((rect) => polygonOf(group, rect));
    if (clipModeOf(group) === 'hidden') {
      polys.forEach((poly) => {
        clipPolylineInside(path, poly).forEach((piece) => {
          dashPolyline(piece).forEach((dash) => pushLine(dash, { color }));
        });
      });
      return;
    }
    const bounds = group.getAttr('clipBounds');
    let pieces = [path];
    if (bounds && bounds.width > 0.2 && bounds.height > 0.2) {
      const boundPoly = polygonOf(group, bounds);
      pieces = pieces.flatMap((piece) => clipPolylineInside(piece, boundPoly));
    }
    polys.forEach((poly) => {
      pieces = pieces.flatMap((piece) => subtractPolyline(piece, poly));
    });
    pieces.forEach((piece) => pushLine(piece, { dashed: false, color }));
  };

  const lineNodes = [];
  const seenLines = new Set();
  ['Line', 'Arrow'].forEach((type) => {
    layer.find(type).forEach((node) => {
      if (seenLines.has(node)) return;
      seenLines.add(node);
      lineNodes.push(node);
    });
  });
  lineNodes.forEach((node) => {
    if (!keepNode(node)) return;
    const raw = node.points();
    const points = [];
    for (let i = 0; i + 1 < raw.length; i += 2) {
      points.push(toModel(node, raw[i], raw[i + 1], stageScale));
    }
    const dashed = Array.isArray(node.dash()) && node.dash().length > 0;
    const color = aciColor(node.stroke());
    const closed = Boolean(node.closed && node.closed());
    if (findClipGroup(node)) {
      emitClipped(node, points, { dashed, closed, color });
    } else {
      pushLine(points, { closed, dashed, color });
    }

    if (node.getClassName() !== 'Arrow' || raw.length < 4) return;
    const pointerLength = node.pointerLength ? node.pointerLength() : 10;
    const pointerWidth = node.pointerWidth ? node.pointerWidth() : 10;
    const addHead = (fromX, fromY, tipX, tipY) => {
      const dx = tipX - fromX;
      const dy = tipY - fromY;
      const length = Math.hypot(dx, dy) || 1;
      const base = [
        tipX - (dx / length) * pointerLength,
        tipY - (dy / length) * pointerLength,
      ];
      const local = arrowHead([tipX, tipY], base, pointerWidth);
      solids.push({
        points: local.map((point) => toModel(node, point[0], point[1], stageScale)),
        color,
      });
    };
    const last = raw.length;
    if (node.pointerAtEnding && node.pointerAtEnding()) {
      addHead(raw[last - 4], raw[last - 3], raw[last - 2], raw[last - 1]);
    }
    if (node.pointerAtBeginning && node.pointerAtBeginning()) {
      addHead(raw[2], raw[3], raw[0], raw[1]);
    }
  });

  layer.find('Rect').forEach((node) => {
    if (!node.visible() || node.opacity() < 0.5 || hasSkipName(node)) return;
    const width = node.width();
    const height = node.height();
    if (Math.abs(width) < 0.05 || Math.abs(height) < 0.05) return;
    const stroke = node.stroke();
    if (!stroke || node.strokeWidth() <= 0) return;
    pushLine([
      toModel(node, 0, 0, stageScale),
      toModel(node, width, 0, stageScale),
      toModel(node, width, height, stageScale),
      toModel(node, 0, height, stageScale),
    ], { closed: true, color: aciColor(stroke) });
  });

  layer.find('Circle').forEach((node) => {
    if (!keepNode(node) || isGreenSnap(node)) return;
    const radius = node.radius();
    if (!(radius > 0.2)) return;
    const center = toModel(node, 0, 0, stageScale);
    const edge = toModel(node, radius, 0, stageScale);
    const scaled = Math.hypot(edge[0] - center[0], edge[1] - center[1]);
    if (scaled < 0.2) return;
    const color = aciColor(node.stroke() || node.fill());
    if (!findClipGroup(node)) {
      circles.push({ center, radius: round(scaled), color });
      return;
    }
    const steps = 48;
    const points = [];
    for (let i = 0; i <= steps; i += 1) {
      const angle = (i / steps) * Math.PI * 2;
      points.push(toModel(node, Math.cos(angle) * radius, Math.sin(angle) * radius, stageScale));
    }
    emitClipped(node, points, { closed: true, color });
  });

  layer.find('Arc').forEach((node) => {
    if (!keepNode(node)) return;
    const radius = node.outerRadius ? node.outerRadius() : node.innerRadius();
    const sweepDeg = node.angle();
    if (!(radius > 0) || !sweepDeg) return;
    const steps = Math.max(8, Math.ceil(Math.abs(sweepDeg) / 6));
    const sweep = (sweepDeg * Math.PI) / 180;
    const points = [];
    for (let i = 0; i <= steps; i += 1) {
      const angle = (sweep * i) / steps;
      points.push(toModel(node, Math.cos(angle) * radius, Math.sin(angle) * radius, stageScale));
    }
    const color = aciColor(node.stroke());
    if (findClipGroup(node)) emitClipped(node, points, { color });
    else pushLine(points, { color });
  });

  layer.find('Text').forEach((node) => {
    if (!node.visible() || node.opacity() < 0.5 || hasSkipName(node)) return;
    const content = String(node.text() || '').replace(/\r/g, '');
    if (!content.trim()) return;
    const fontSize = node.fontSize() || 0;
    if (fontSize <= 0) return;
    const origin = toModel(node, 0, 0, stageScale);
    const down = toModel(node, 0, fontSize, stageScale);
    const height = Math.hypot(down[0] - origin[0], down[1] - origin[1]);
    if (height < 0.5) return;
    const parts = content.split('\n');
    const blockHeight = node.height() || fontSize * parts.length;
    const lineHeight = blockHeight / parts.length;
    const width = node.width() || fontSize;
    parts.forEach((part, index) => {
      if (!part.trim()) return;
      const center = toModel(node, width / 2, index * lineHeight + lineHeight / 2, stageScale);
      texts.push({
        text: part,
        x: center[0],
        y: center[1],
        height: round(height),
        rotation: round(node.getAbsoluteRotation()),
        color: aciColor(node.fill()),
      });
    });
  });

  return { lines, circles, solids, texts };
}

export async function exportStageToDWG(stage, { fileName = 'Чертёж', fileHandle = null } = {}) {
  if (!stage) {
    throw new Error('Холст недоступен');
  }
  const entities = collectDrawingEntities(stage);
  const count = entities.lines.length + entities.circles.length + entities.texts.length;
  if (count === 0) {
    throw new Error('На чертеже нет линий для сохранения');
  }

  const response = await fetch(`${BACKEND_URL}/generate-dwg`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      filename: `${fileName}.dwg`,
      ...entities,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || `Ошибка сервера: ${response.status}`);
  }

  const blob = await response.blob();
  await saveBlobAsFile(
    blob,
    `${fileName}.dwg`,
    'DWG чертёж',
    { 'application/acad': ['.dwg'] },
    null,
    fileHandle,
  );
  return true;
}
